import "./index.css";
import { useThrottleEffect } from "ahooks";
import { DownOutlined } from "@ant-design/icons";
import React, { useEffect, useRef, useState } from "react";

interface TypeDeepSearchThinkingProps {
  /**
   * @param startTime 思考开始时间
   */
  startTime?: number;
  /**
   * @param startTime 思考结束时间
   */
  endTime?: number;
  /**
   * @param content 思考内容
   * @description 可能存在多条，所以用数组
   */
  content: string[];
}

/**
 * @name DeepSearchThinking 深度思考内容展示
 */
const DeepSearchThinking: React.FC<TypeDeepSearchThinkingProps> = ({
  content,
  endTime,
  startTime,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [expand, setExpand] = useState(true);
  const [usedTime, setUsedTime] = useState(0);

  function toBottom() {
    if (!expand) return;
    const el = ref.current;
    if (el) {
      // Use requestAnimationFrame to ensure smooth scrolling
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
  }

  function onExpandChange() {
    setExpand((v) => !v);
  }

  // Use a shorter throttle time for more responsive scrolling
  useThrottleEffect(toBottom, [content], { wait: 100 });

  // Monitor content length changes specifically
  useEffect(() => {
    toBottom();
  }, [content?.length]);

  // Monitor the actual text content changes
  useEffect(() => {
    toBottom();
  }, [content?.join("")]);

  useEffect(() => {
    toBottom();
  }, [expand]);

  // Use MutationObserver to detect DOM changes for more reliable scrolling
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new MutationObserver(() => {
      toBottom();
    });

    observer.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [expand]);

  useEffect(() => {
    // 如果有结束时间 直接算出结束时间
    if (endTime) {
      setUsedTime(Math.floor((endTime - startTime) / 1000));
    }
  }, [usedTime, endTime, startTime]);

  const LENGTH_CONTENT = content?.length || 0;

  useEffect(()=>{
    endTime && onExpandChange();
  },[endTime]);

  return (
    <div className="deep-search-layout">
      <div className="deep-search-controller">
        {endTime ? (
          <span>Thought for {usedTime > 59 ? `60+` : usedTime} seconds</span>
        ) : startTime ? (
          <span>
            Thinking &nbsp;
            <span className="typing-indicator">
              <span className="dot">.</span>
              <span className="dot">.</span>
              <span className="dot">.</span>
            </span>
          </span>
        ) : (
          <span>Deep thinking content</span>
        )}

        <DownOutlined
          rev={undefined}
          onClick={onExpandChange}
          style={{ transform: `rotate(${expand ? 180 : 360}deg)` }}
        />
      </div>

      {expand && (
        <div className="deep-search-thinking">
          <div ref={ref} className="deep-search-thinking-ctx">
            <i className="deep-search-thinking-dot" />
            <i className="deep-search-thinking-dot" />
            {content?.map((v, i) => {
              // Filter out content containing </Thinking
              const filteredContent = v.replace(/<\/Thinking/g, "");
              const LOCATION = i + 1;
              return (
                <div key={v}>
                  {filteredContent}
                  {LENGTH_CONTENT === LOCATION ? null : <p />}
                </div>
              );
            })}

            {/* {endTime ? (
              <p className="deep-search-thinking-finished">Finished</p>
            ) : null} */}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeepSearchThinking;
